import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  XOctagon,
  User,
  Building2,
  CreditCard,
  Shield,
  FileCode,
  DollarSign
} from 'lucide-react';
import { closureApi } from '../../services/iso20022Api';
import {
  formatDate,
  copyToClipboard,
  downloadFile
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const ClosureDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [closure, setClosure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);

  useEffect(() => {
    loadClosure();
  }, [id]);

  const loadClosure = async () => {
    try {
      setLoading(true);
      const data = await closureApi.getClosureById(id);
      setClosure(data);
    } catch (err) {
      console.error('Failed to load closure:', err);
      setError('Closure not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (field, value) => {
    const success = await copyToClipboard(value);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownloadXml = () => {
    if (closure?.rawXml) {
      const fileName = `closure-acmt019-${closure.id}.xml`;
      downloadFile(closure.rawXml, fileName, 'application/xml');
    }
  };

  const handleViewXml = () => {
    setXmlModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading closure details...</p>
        </div>
      </div>
    );
  }

  if (error || !closure) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Closure Not Found</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate('/iso20022/closures')} className="btn-primary">
            Back to Closures
          </button>
        </div>
      </div>
    );
  };

  const InfoRow = ({ label, value, copyable = false, fieldName = '' }) => (
    <div className="py-3 border-b border-gray-200 last:border-0">
      <div className="flex items-center justify-between">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900 font-mono flex items-center space-x-2">
          <span>{value || 'N/A'}</span>
          {copyable && value && (
            <button
              onClick={() => handleCopy(fieldName, value)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Copy to clipboard"
            >
              {copiedField === fieldName ? (
                <CheckCircle className="w-4 h-4 text-success-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          )}
        </dd>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const statusClasses = {
      'COMPLETED': 'bg-success-100 text-success-800',
      'CLOSED': 'bg-success-100 text-success-800',
      'SIGNED': 'bg-success-100 text-success-800',
      'PROCESSING': 'bg-warning-100 text-warning-800',
      'PENDING': 'bg-warning-100 text-warning-800',
      'RECEIVED': 'bg-info-100 text-info-800',
      'SENT': 'bg-info-100 text-info-800',
      'REJECTED': 'bg-danger-100 text-danger-800',
      'FAILED': 'bg-danger-100 text-danger-800'
    };

    const badgeClass = statusClasses[status?.toUpperCase()] || 'bg-gray-100 text-gray-800';
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
        {status || 'Unknown'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/closures')}
            className="btn-secondary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Account Closure #{closure.id}
            </h1>
            <p className="text-gray-600 mt-1">Account closing request details (acmt.019)</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {closure.rawXml && (
            <>
              <button
                onClick={handleViewXml}
                className="btn-secondary flex items-center space-x-2"
              >
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
              <button
                onClick={handleDownloadXml}
                className="btn-secondary flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download</span>
              </button>
            </>
          )}
          <button
            onClick={loadClosure}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Card */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-lg bg-red-100">
                <XOctagon className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Status</p>
                <div className="mt-1">
                  {getStatusBadge(closure.statusLabel || closure.status)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Created</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(closure.createdAt, 'PPp')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <CreditCard className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-1">
              <InfoRow 
                label="Account ID" 
                value={closure.accountId} 
                copyable 
                fieldName="accountId"
              />
              <InfoRow 
                label="Account Name" 
                value={closure.accountName} 
                copyable 
                fieldName="accountName"
              />
              <InfoRow 
                label="Currency" 
                value={closure.accountCurrency}
              />
              <InfoRow 
                label="Account Servicer ID" 
                value={closure.accountServicerId} 
                copyable 
                fieldName="accountServicerId"
              />
            </dl>
          </div>
        </div>

        {/* Organization Information */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Organization Information</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-1">
              <InfoRow 
                label="Legal Name" 
                value={closure.orgLegalName} 
                copyable 
                fieldName="orgLegalName"
              />
              <InfoRow 
                label="Merchant Code" 
                value={closure.merchantCode} 
                copyable 
                fieldName="merchantCode"
              />
            </dl>
          </div>
        </div>

        {/* Message Details */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <FileCode className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Message Details</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-1">
              <InfoRow 
                label="Message ID" 
                value={closure.msgId} 
                copyable 
                fieldName="msgId"
              />
              <InfoRow 
                label="Process ID" 
                value={closure.procId} 
                copyable 
                fieldName="procId"
              />
              <InfoRow 
                label="Message Type" 
                value={closure.msgType || 'acmt.019'}
              />
              <InfoRow 
                label="Direction" 
                value={closure.directionLabel || closure.direction}
              />
            </dl>
          </div>
        </div>

        {/* Security & Signature */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Security & Signature</h2>
            </div>
          </div>
          <div className="card-body">
            <dl className="space-y-1">
              <div className="py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <dt className="text-sm font-medium text-gray-500">Signature Valid</dt>
                  <dd className="flex items-center space-x-2">
                    {closure.signatureOk === true ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-success-600" />
                        <span className="text-sm font-medium text-success-600">Valid</span>
                      </>
                    ) : closure.signatureOk === false ? (
                      <>
                        <XCircle className="w-5 h-5 text-danger-600" />
                        <span className="text-sm font-medium text-danger-600">Invalid</span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-400">Not Signed</span>
                    )}
                  </dd>
                </div>
              </div>
              <InfoRow 
                label="Signature Method" 
                value={closure.signatureMethod}
              />
              <InfoRow 
                label="Digest Method" 
                value={closure.digestMethod}
              />
              <InfoRow 
                label="Canonicalization" 
                value={closure.canonicalizationMethod}
              />
            </dl>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Timestamps</h2>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Created At</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(closure.createdAt, 'PPpp')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Updated At</p>
              <p className="text-sm font-medium text-gray-900">
                {formatDate(closure.updatedAt, 'PPpp')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Raw Data (for debugging) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Debug: Raw Data</h2>
          </div>
          <div className="card-body">
            <pre className="text-xs bg-gray-50 p-4 rounded overflow-x-auto">
              {JSON.stringify(closure, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {/* XML Viewer Modal */}
      {closure.rawXml && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => setXmlModalOpen(false)}
          xmlContent={closure.rawXml}
          fileName={`closure-acmt019-${closure.id}.xml`}
          title={`Account Closure #${closure.id}`}
        />
      )}
    </div>
  );
};

export default ClosureDetail;
